import { bengaluruBounds } from "@/info/bengaluru-gps";
import type { LocationCoordinates } from "@/stores/location-permission-store";

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param coord1 First coordinate
 * @param coord2 Second coordinate
 * @returns Distance in meters
 */
export function calculateDistance(coord1: LocationCoordinates, coord2: LocationCoordinates): number {
	const R = 6371e3; // Earth's radius in meters
	const φ1 = (coord1.latitude * Math.PI) / 180;
	const φ2 = (coord2.latitude * Math.PI) / 180;
	const Δφ = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
	const Δλ = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

	const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c;
}

/**
 * Check if a coordinate is within a bounding box
 */
export function isWithinBounds(
	coord: LocationCoordinates,
	bounds: {
		north: number;
		south: number;
		east: number;
		west: number;
	},
): boolean {
	return (
		coord.latitude >= bounds.south &&
		coord.latitude <= bounds.north &&
		coord.longitude >= bounds.west &&
		coord.longitude <= bounds.east
	);
}

/**
 * Check if a coordinate is within a circular area
 */
export function isWithinRadius(coord: LocationCoordinates, center: LocationCoordinates, radiusMeters: number): boolean {
	const distance = calculateDistance(coord, center);
	return distance <= radiusMeters;
}

/**
 * Format coordinates for display
 */
export function formatCoordinates(coord: LocationCoordinates, precision = 6): string {
	return `${coord.latitude.toFixed(precision)}, ${coord.longitude.toFixed(precision)}`;
}

/**
 * Get accuracy description based on accuracy value
 */
export function getAccuracyDescription(accuracy?: number): string {
	if (!accuracy) return "Unknown accuracy";

	if (accuracy <= 5) return "Very high accuracy";
	if (accuracy <= 20) return "High accuracy";
	if (accuracy <= 100) return "Good accuracy";
	if (accuracy <= 1000) return "Low accuracy";
	return "Very low accuracy";
}

/**
 * Convert coordinates to Google Maps URL
 */
export function toGoogleMapsUrl(coord: LocationCoordinates, zoom = 15): string {
	return `https://www.google.com/maps/@${coord.latitude},${coord.longitude},${zoom}z`;
}

/**
 * Convert coordinates to Apple Maps URL
 */
export function toAppleMapsUrl(coord: LocationCoordinates): string {
	return `https://maps.apple.com/?ll=${coord.latitude},${coord.longitude}`;
}

/**
 * Get location age in human readable format
 */
export function formatLocationAge(ageMs: number): string {
	const seconds = Math.floor(ageMs / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
	if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
	if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
	return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
}

/**
 * Check if location is within Bangalore
 */
export function isLocationInBangalore(coord: LocationCoordinates): boolean {
	return isWithinBounds(coord, bengaluruBounds);
}

/**
 * Get the closest point within Bangalore bounds
 */
export function getClosestBangalorePoint(coord: LocationCoordinates): LocationCoordinates {
	return {
		latitude: Math.max(bengaluruBounds.south, Math.min(bengaluruBounds.north, coord.latitude)),
		longitude: Math.max(bengaluruBounds.west, Math.min(bengaluruBounds.east, coord.longitude)),
	};
}

/**
 * Common position options presets
 */
export const POSITION_OPTIONS = {
	// High accuracy for precise location (uses GPS)
	HIGH_ACCURACY: {
		enableHighAccuracy: true,
		timeout: 15000,
		maximumAge: 60000, // 1 minute
	} as PositionOptions,

	// Balanced accuracy and speed
	BALANCED: {
		enableHighAccuracy: false,
		timeout: 10000,
		maximumAge: 300000, // 5 minutes
	} as PositionOptions,

	// Fast location (may be less accurate)
	FAST: {
		enableHighAccuracy: false,
		timeout: 5000,
		maximumAge: 600000, // 10 minutes
	} as PositionOptions,

	// Fresh location (no cache)
	FRESH: {
		enableHighAccuracy: false,
		timeout: 10000,
		maximumAge: 0,
	} as PositionOptions,
} as const;
